/**
 * Custom Chord Utilities
 *
 * Utilities for handling custom chord text that bypasses music theory parsing.
 */
import { buildChordString } from './chordBuilder.js';
/** Maximum length of a slot's custom chord text ("N.C.", "Tacet", …). */
export const CUSTOM_TEXT_MAX_CHARS = 20;
/**
 * Validates custom chord text
 *
 * @param text - The custom chord text to validate
 * @returns Error message if invalid, null if valid
 *
 * Validation rules:
 * - Text cannot contain control characters (ASCII 0-31, 127) or newlines (checked first)
 * - Text cannot be empty (after trimming)
 * - Text cannot exceed 20 characters
 */
export function validateCustomText(text) {
    // Check for control characters FIRST (before trimming)
    // Regex matches ASCII control characters (0-31) and DEL (127)
    // This includes: newlines (\n), tabs (\t), carriage returns (\r), null (\x00), etc.
    if (/[\x00-\x1F\x7F]/.test(text)) {
        return 'Custom chord text cannot contain control characters or newlines';
    }
    // Check length before trimming (user sees the actual length)
    if (text.length > CUSTOM_TEXT_MAX_CHARS) {
        return 'Custom chord text cannot exceed 20 characters';
    }
    // Check if empty after trimming
    const trimmed = text.trim();
    if (trimmed === '') {
        return 'Custom chord text cannot be empty';
    }
    return null;
}
/**
 * Gets the display text for a chord slot
 *
 * Priority order:
 * 1. Custom text (if present)
 * 2. Built chord string (if chord object present)
 * 3. Empty slot indicator ("- - -")
 *
 * @param slot - The slot state containing chord or custom text
 * @returns The text to display for the chord
 */
export function getChordDisplayText(slot) {
    // Priority 1: Custom text
    if (slot.customText) {
        return slot.customText;
    }
    // Priority 2: Built chord string
    if (slot.chord) {
        return buildChordString(slot.chord);
    }
    // Priority 3: Empty slot
    return "- - -";
}
